import { Injectable, NgZone } from "@angular/core";
import { Router } from "@angular/router";
import { Subject, Subscription } from "rxjs";
import { IAdminSystemAlert } from "../../../_models/notification.model";
import { SocketService } from "../../socket/socket.service";

export const ADMIN_NOTIFICATIONS_ROUTE = "/admin/notifications";

/**
 * Single owner of live alert fan-out.
 *
 * `incomingAlert$` always fires so the notifications screen can animate the new
 * card in. `popupAlert$` only fires when the admin is NOT already on the
 * notifications screen, which is what keeps the grey backdrop from appearing
 * over the list the admin is reading.
 */
@Injectable({ providedIn: "root" })
export class NotificationCenterService {
  private incomingAlertSubject = new Subject<IAdminSystemAlert>();
  public incomingAlert$ = this.incomingAlertSubject.asObservable();

  private popupAlertSubject = new Subject<IAdminSystemAlert>();
  public popupAlert$ = this.popupAlertSubject.asObservable();

  private sub?: Subscription;

  constructor(
    private socketService: SocketService,
    private router: Router,
    private ngZone: NgZone
  ) {}

  /** Called from the admin layout so the service is alive for the whole session. */
  init(): void {
    if (this.sub) return;

    this.sub = this.socketService.adminNewAlert$.subscribe((alert) => {
      this.ngZone.run(() => this.publish(alert));
    });
  }

  private publish(alert: IAdminSystemAlert): void {
    if (!alert) return;

    this.incomingAlertSubject.next(alert);

    if (!this.isOnNotificationsScreen()) {
      this.popupAlertSubject.next(alert);
    }
  }

  isOnNotificationsScreen(): boolean {
    const url = (this.router.url || "").split("?")[0].split("#")[0];
    return url === ADMIN_NOTIFICATIONS_ROUTE || url.startsWith(`${ADMIN_NOTIFICATIONS_ROUTE}/`);
  }
}
